import useSWR from "swr"
import { useMemo, useState } from "preact/hooks"
import { X } from "lucide-react"
import { getHierarchyBreakdown } from "../api/distributor_sales_api"
import { ChartMetric, HierarchyNode, SalesMetrics } from "../types/distributor_sales"
import { DistributorSalesInteractivePie } from "./distributor_sales_interactive_pie"
import { availablePieMetrics, PieMetricSelect } from "./distributor_sales_pie_shared"

interface HierarchyPieModalProps {
  node: HierarchyNode
  reportId?: string
  source?: string
  onClose: () => void
}

function toNumber(value: number | string | undefined): number {
  if (value === undefined) return 0
  if (typeof value === "string") return parseFloat(value) || 0
  return value
}

function getSliceValue(metrics: SalesMetrics, metric: ChartMetric): number {
  if (metric === "ordered_vendor_cost") return toNumber(metrics.ordered_vendor_cost)
  if (metric === "shipped_vendor_cost") return toNumber(metrics.shipped_vendor_cost)
  if (metric === "ordered_list_wholesale") return toNumber(metrics.ordered_list_wholesale)
  if (metric === "shipped_list_wholesale") return toNumber(metrics.shipped_list_wholesale)
  if (metric === "ordered_quantity") return metrics.ordered_quantity ?? 0
  if (metric === "shipped_quantity") return metrics.shipped_quantity ?? 0
  return metrics.stores_ordered ?? 0
}

function pieSubtitle(parentLevel: number): string {
  const labels: Record<number, string> = {
    0: "Breakdown by retailer",
    1: "Sales by region",
    2: "Sales by banner / retailer",
    3: "Sales by store",
    4: "Sales by SKU",
  }
  return labels[parentLevel] ?? "Breakdown"
}

export function HierarchyPieModal({ node, reportId, onClose }: HierarchyPieModalProps) {
  const [metric, setMetric] = useState<ChartMetric>("shipped_vendor_cost")

  const { data, isLoading } = useSWR(
    reportId ? ["hierarchy-breakdown", reportId, node.id] : null,
    () => getHierarchyBreakdown({ report_id: reportId as string, node_id: node.id }),
    { revalidateOnFocus: false }
  )

  const pieSlices = useMemo(() => {
    if (!data?.segments.length) return []
    return data.segments
      .map((s: { segment: string; metrics: SalesMetrics }) => ({
        name: s.segment,
        value: getSliceValue(s.metrics, metric),
      }))
      .filter((s: { value: number }) => s.value > 0)
      .sort((a: { value: number }, b: { value: number }) => b.value - a.value)
  }, [data?.segments, metric])

  const subtitle = data ? pieSubtitle(data.parent_level) : ""

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e: MouseEvent) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-6 relative">
        <button
          class="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div class="flex items-center justify-between mb-4 pr-8">
          <div>
            <h2 class="text-lg font-semibold">{node.name}</h2>
            <p class="text-sm text-gray-500">{subtitle}</p>
          </div>
          <PieMetricSelect
            value={metric}
            onChange={setMetric}
            metrics={data ? availablePieMetrics(
              data.segments.map((s: { metrics: SalesMetrics }) => s.metrics),
              getSliceValue,
              metric,
            ) : undefined}
          />
        </div>

        {!reportId ? (
          <div class="h-[300px] flex items-center justify-center text-gray-400">Select a table report first.</div>
        ) : isLoading ? (
          <div class="h-[300px] flex items-center justify-center text-gray-400">Loading…</div>
        ) : pieSlices.length === 0 ? (
          <div class="h-[300px] flex items-center justify-center text-gray-400">
            No breakdown data for this row.
          </div>
        ) : (
          <DistributorSalesInteractivePie
            key={`${reportId}-${node.id}`}
            slices={pieSlices}
            metric={metric}
            layout="modal"
            rowClassName="flex-1 min-h-0"
          />
        )}
      </div>
    </div>
  )
}

/** @deprecated use HierarchyPieModal */
export const RegionPieModal = HierarchyPieModal
